import type { Device } from '@studio/models/device';

/**
 * Client-only calibration cancel-gate registry (TD3, ENG-4026).
 *
 * Records the epoch-ms of the most recent user-initiated cancel per device so
 * the `<Calibration>` watchdog can suppress a `calibration_completed` emit that
 * lands within the gate window — the renderer cannot distinguish cancel-cleanup
 * from a real completion (see `decideCalibrationCompleted`).
 *
 * Module-level (NOT depot, NOT watchdog-local) on purpose: the PRIMARY cancel
 * path is the "Calibrating…" badge X -> `control/calibration/commands/stop.ts`,
 * which lives OUTSIDE the `<Calibration>` watchdog component and so cannot reach
 * a watchdog-local `Map`. Holding the gate in this shared module lets BOTH
 * `stop.ts` and the watchdog (its in-prompt "Use Generic Driver" / "Skip"
 * paths) arm the same gate. Mirrors the sibling `attempt-id.ts` registry.
 *
 * Per knowledge: td3-cancel-gate (ai.knowledge/ENG-4026). Removable in one file
 * when the bridge proto carries `CALIBRATION_STATE_CANCELLED` (RFD follow-up).
 *
 * NOTE (ENG-4026): intentionally NOT re-exported through `commands/index.ts` —
 * consumers import directly. Widening that barrel trips a latent jest ESM-linker
 * bug in unrelated suites (see the barrel comment in `commands/index.ts`).
 */
const lastCancelMs = new Map<Device['id'], number>();

/**
 * Arm the cancel gate for a device. `atMs` is injectable so tests drive it with
 * fake timers deterministically; production callers use the `Date.now()` default.
 */
export const recordCalibrationCancel = (
  deviceId: Device['id'],
  atMs: number = Date.now()
): void => {
  lastCancelMs.set(deviceId, atMs);
};

/** Epoch ms of the most recent cancel for a device (`undefined` = none). */
export const getLastCalibrationCancelMs = (deviceId: Device['id']): number | undefined =>
  lastCancelMs.get(deviceId);

/** Clear the cancel gate for a device (explicit resets + test hygiene). */
export const clearCalibrationCancel = (deviceId: Device['id']): void => {
  lastCancelMs.delete(deviceId);
};
